import { useEffect, useState } from "react";
import { ConfirmButton } from "@/components/common/ConfirmButton";
import { Spinner } from "@/components/common/Spinner";
import { listOrganizations, type Organization } from "@/lib/api";
import { dateFormatter } from "@/lib/format-datetime";
import { CreateOrganizationDialog } from "./CreateOrganizationDialog";

export function OrganizationsTab() {
	const [orgs, setOrgs] = useState<Organization[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [refreshKey, setRefreshKey] = useState(0);
	const [createOpen, setCreateOpen] = useState(false);

	useEffect(() => {
		let cancelled = false;
		setLoading(true);
		setError(null);
		listOrganizations()
			.then((data) => {
				if (!cancelled) setOrgs(data);
			})
			.catch((err: Error) => {
				if (!cancelled) setError(err.message);
			})
			.finally(() => {
				if (!cancelled) setLoading(false);
			});
		return () => {
			cancelled = true;
		};
	}, [refreshKey]);

	return (
		<div style={{ padding: 32, height: "100%", overflowY: "auto" }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					height: 40,
					flexShrink: 0,
					marginBottom: 24,
				}}
			>
				<h2
					style={{
						fontSize: "var(--text-heading)",
						fontWeight: 600,
						letterSpacing: "var(--tracking-snug)",
						color: "var(--fg-1)",
						margin: 0,
					}}
				>
					組織
				</h2>
				<ConfirmButton
					label="新規組織"
					icon="plus"
					size="sm"
					loading={false}
					onClick={() => setCreateOpen(true)}
				/>
			</div>
			<CreateOrganizationDialog
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				onCreated={() => setRefreshKey((k) => k + 1)}
			/>
			{error && <p style={{ color: "var(--danger)", fontSize: "var(--text-ui)" }}>{error}</p>}
			{!error && (
				<div
					style={{
						boxShadow: "var(--shadow-ring)",
						borderRadius: "var(--radius-4)",
						overflow: "hidden",
					}}
				>
					<table style={{ width: "100%", borderCollapse: "collapse" }}>
						<thead>
							<tr style={{ background: "var(--surface-2)" }}>
								<th
									className="text-caption text-fg-3"
									style={{
										padding: "10px 16px",
										textAlign: "left",
										color: "var(--fg-3)",
										fontWeight: 600,
										letterSpacing: "var(--tracking-label)",
										textTransform: "uppercase",
										borderBottom: "var(--pane-header-border)",
										width: 180,
									}}
								>
									ID
								</th>
								<th
									className="text-caption text-fg-3"
									style={{
										padding: "10px 16px",
										textAlign: "left",
										color: "var(--fg-3)",
										fontWeight: 600,
										letterSpacing: "var(--tracking-label)",
										textTransform: "uppercase",
										borderBottom: "var(--pane-header-border)",
									}}
								>
									組織名
								</th>
								<th
									className="text-caption text-fg-3"
									style={{
										padding: "10px 16px",
										textAlign: "left",
										color: "var(--fg-3)",
										fontWeight: 600,
										letterSpacing: "var(--tracking-label)",
										textTransform: "uppercase",
										borderBottom: "var(--pane-header-border)",
										whiteSpace: "nowrap",
									}}
								>
									作成日時
								</th>
							</tr>
						</thead>
						<tbody className="select-text">
							{loading ? (
								<tr style={{ height: 44 }}>
									<td colSpan={3} style={{ textAlign: "center", verticalAlign: "middle" }}>
										<Spinner size={14} />
									</td>
								</tr>
							) : orgs.length === 0 ? (
								<tr>
									<td
										colSpan={3}
										style={{
											padding: "24px 16px",
											color: "var(--fg-3)",
											fontSize: "var(--text-ui)",
											textAlign: "center",
										}}
									>
										組織がありません
									</td>
								</tr>
							) : (
								orgs.map((org, i) => (
									<tr
										key={org.id}
										style={{
											height: 44,
											borderBottom: i < orgs.length - 1 ? "var(--pane-header-border)" : "none",
										}}
										onMouseEnter={(e) => {
											(e.currentTarget as HTMLTableRowElement).style.background =
												"var(--surface-2)";
										}}
										onMouseLeave={(e) => {
											(e.currentTarget as HTMLTableRowElement).style.background = "transparent";
										}}
									>
										<td
											className="font-mono text-title text-fg-2"
											style={{
												padding: "0 16px",
												color: "var(--fg-2)",
												fontSize: "var(--text-caption)",
											}}
										>
											{org.id}
										</td>
										<td
											style={{
												padding: "0 16px",
												color: "var(--fg-1)",
												fontSize: "var(--text-caption)",
											}}
										>
											{org.name}
										</td>
										<td
											style={{
												padding: "0 16px",
												color: "var(--fg-2)",
												fontSize: "var(--text-caption)",
											}}
										>
											{dateFormatter.format(new Date(org.createdAt))}
										</td>
									</tr>
								))
							)}
						</tbody>
					</table>
				</div>
			)}
		</div>
	);
}
